import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { rtctApi } from "services/rtctApi";
import {
  analysisMaxPoints,
  analysisPoints,
  analysisThinning,
  readProfile,
} from "utils/radarAnalysis";
import { finite, timeOf } from "utils/radarDiagnostics";
import { readSuggestedGate } from "utils/radarEdge";
import { readRain } from "utils/radarRain";
import {
  analysisPending,
  burstKey,
  loadStart,
  mergeBursts,
  pageBursts,
  pendingSince,
  pollSince,
} from "utils/radarBurst";
import {
  RECENT_MS,
  STREAM_LIMIT,
  STREAM_POLL_MS,
  WATERFALL_COLUMNS,
  appendCaptures,
  decodeWaterfall,
  mergeCaptures,
} from "utils/radarStream";

// Radar data for the Radar analysis page: one stored capture, the live
// stream, the waterfall, the capture behind a waterfall column, the bursts,
// the analysis of the bursts, the empty-pipe profile and rain at the node.
// Hidden tabs ask nothing.
const hidden = () => document.visibilityState === "hidden";
// Analyses still pending (the server analyses a few bursts a request, the
// newest first): asked for again every 5 s, until a few asks in a row bring
// nothing new.
export const ANALYSIS_POLL_MS = 5000;
export const ANALYSIS_TRIES = 6;
const CACHE_LIMIT = 40;
const remember = (cache, id, data) => {
  cache.current.set(id, data);
  while (cache.current.size > CACHE_LIMIT)
    cache.current.delete(cache.current.keys().next().value);
};

// One capture's full record (radar-captures/<id>/), loaded once and kept in
// `cache` (a ref holding a Map; the oldest entries go beyond 40).
export function useRadarCaptureDetail(id, refresh, cache) {
  const [state, setState] = useState({ id: null, data: null, error: "" });
  useEffect(() => {
    if (!id) return;
    const hit = cache.current.get(id);
    if (hit) {
      setState((old) =>
        old.id === id && old.data === hit ? old : { id, data: hit, error: "" },
      );
      return;
    }
    const abort = new AbortController();
    rtctApi
      .radarCapture(id, abort.signal)
      .then((data) => {
        if (abort.signal.aborted) return;
        remember(cache, id, data);
        setState({ id, data, error: "" });
      })
      .catch((e) => {
        if (!abort.signal.aborted)
          setState({ id, data: null, error: e.message });
      });
    return () => abort.abort();
  }, [id, refresh, cache]);
  return state.id === id ? state : { id, data: null, error: "" };
}

// The live stream: captures of the last ten minutes, then only newer ones
// (since = the newest capture's own time), every 5 s while `fast` or while
// captures keep arriving (the newest within `keepFastWithinMs`), otherwise
// with the page's refresh. A full page is followed at once by the next.
// `offset` is the server's clock minus this browser's; `unit` is the server's
// description of the dB values (db_unit).
export function useRadarStream(
  mac,
  { fast = false, keepFastWithinMs = 0, refresh = 0 } = {},
) {
  const [state, setState] = useState({
    captures: [],
    error: null,
    loaded: false,
    offset: 0,
    unit: null,
  });
  const [tick, setTick] = useState(0);
  const cursor = useRef(null);
  useEffect(() => {
    if (!mac || hidden()) return;
    const abort = new AbortController();
    const since =
      cursor.current || new Date(Date.now() - RECENT_MS).toISOString();
    rtctApi
      .radarStream(mac, abort.signal, { since, limit: STREAM_LIMIT })
      .then((data) => {
        if (abort.signal.aborted) return;
        const incoming = Array.isArray(data?.captures)
          ? data.captures.filter((c) => timeOf(c?.t) !== null)
          : [];
        const server = timeOf(data?.server_time);
        const offset = server === null ? 0 : server - Date.now();
        if (incoming.length) cursor.current = incoming[incoming.length - 1].t;
        setState((old) => ({
          captures: mergeCaptures(
            old.captures,
            incoming,
            Date.now() + offset - RECENT_MS,
          ),
          error: null,
          loaded: true,
          offset,
          unit: typeof data?.db_unit === "string" ? data.db_unit : old.unit,
        }));
        if (incoming.length >= STREAM_LIMIT) setTick((n) => n + 1);
      })
      .catch((e) => {
        if (!abort.signal.aborted)
          setState((old) => ({
            ...old,
            error: { message: e.message, status: e.status },
            loaded: true,
          }));
      });
    return () => abort.abort();
  }, [mac, refresh, tick]);
  const newest = state.captures.length
    ? timeOf(state.captures[state.captures.length - 1].t)
    : null;
  const polling =
    fast ||
    (keepFastWithinMs > 0 &&
      newest !== null &&
      Date.now() + state.offset - newest < keepFastWithinMs);
  useEffect(() => {
    if (!polling) return;
    const timer = setInterval(() => {
      if (!hidden()) setTick((n) => n + 1);
    }, STREAM_POLL_MS);
    return () => clearInterval(timer);
  }, [polling]);
  return state;
}

// How stale a waterfall may get before the page's refresh reloads it: about
// a column's worth of its window, between one and five minutes. Captures that
// arrive in between are added as columns.
export const waterfallReloadMs = (minutes) =>
  Math.min(300000, Math.max(60000, minutes * 2000));

export function useRadarWaterfall(
  mac,
  minutes,
  { refresh = 0, captures = [], offset = 0 } = {},
) {
  const [state, setState] = useState({ key: null, data: null, error: null });
  const [reload, setReload] = useState(0);
  const fetchedAt = useRef(0);
  const firstRefresh = useRef(refresh);
  const latest = useRef({ captures, offset });
  latest.current = { captures, offset };
  const key = `${mac}|${minutes}`;
  useEffect(() => {
    if (!mac || hidden()) return;
    const abort = new AbortController();
    const id = `${mac}|${minutes}`;
    rtctApi
      .radarWaterfall(mac, abort.signal, {
        minutes,
        maxCols: WATERFALL_COLUMNS,
      })
      .then((data) => {
        if (abort.signal.aborted) return;
        fetchedAt.current = Date.now();
        const now = Date.now() + latest.current.offset;
        setState({
          key: id,
          data: appendCaptures(
            decodeWaterfall(data),
            latest.current.captures,
            now - minutes * 60000,
          ),
          error: null,
        });
      })
      .catch((e) => {
        if (!abort.signal.aborted)
          setState((old) => ({
            key: id,
            data: old.key === id ? old.data : null,
            error: { message: e.message, status: e.status },
          }));
      });
    return () => abort.abort();
  }, [mac, minutes, reload]);
  useEffect(() => {
    if (refresh === firstRefresh.current) return;
    if (Date.now() - fetchedAt.current >= waterfallReloadMs(minutes))
      setReload((n) => n + 1);
  }, [refresh, minutes]);
  useEffect(() => {
    setState((old) => {
      if (!old.data) return old;
      const next = appendCaptures(
        old.data,
        captures,
        Date.now() + offset - minutes * 60000,
      );
      return next === old.data ? old : { ...old, data: next };
    });
  }, [captures, offset, minutes]);
  return state.key === key ? state : { key, data: null, error: null };
}

// The capture behind a point in time (a waterfall column): the first stored
// capture at or after it, if it lies within `toleranceMs`; `missing` when
// there is none (thinned out, or no longer stored). Answers are kept in `cache`.
export function useRadarCaptureAt(mac, target, toleranceMs, cache) {
  const [state, setState] = useState({
    key: null,
    capture: null,
    missing: false,
    error: "",
  });
  const key = target?.key ?? null;
  const ms = target?.ms ?? null;
  useEffect(() => {
    if (!mac || key === null || ms === null) return;
    const hit = cache.current.get(key);
    if (hit) {
      setState(hit);
      return;
    }
    const abort = new AbortController();
    rtctApi
      .radarStream(mac, abort.signal, {
        since: new Date(ms - 1).toISOString(),
        limit: 1,
      })
      .then((data) => {
        if (abort.signal.aborted) return;
        const first = Array.isArray(data?.captures) ? data.captures[0] : null;
        const t = timeOf(first?.t);
        const capture =
          t !== null && Math.abs(t - ms) <= toleranceMs ? first : null;
        const next = { key, capture, missing: !capture, error: "" };
        remember(cache, key, next);
        setState(next);
      })
      .catch((e) => {
        if (!abort.signal.aborted)
          setState({ key, capture: null, missing: false, error: e.message });
      });
    return () => abort.abort();
  }, [mac, key, ms, toleranceMs, cache]);
  return state.key === key
    ? state
    : { key, capture: null, missing: false, error: "", loading: key !== null };
}

// Rich wakes and rich readings: the bursts from `from` (ms since the epoch)
// on, newest first. The first load pages forward from `from` (a window over
// two days long: from a day ago first); a `from` further back extends the
// window (paging forward until it reaches the bursts already here), back by
// no more bursts than one load takes at the density held (loadStart(): rich
// readings bring one a wake);
// each change of `refresh` asks only for bursts after the newest t_last held
// (a burst still filling comes back with each new capture), never from a
// hidden tab and never while a load runs. `offset` is the server's clock
// minus this browser's; `covered` is how far back the bursts are loaded;
// `truncated` is "cap" when a window held more bursts than one load takes
// (then `covered` says how far back they reach). `kind` ("rich" or "burst")
// loads one kind (the endpoint's ?kind=); without it, both.
const iso = (ms) => new Date(ms).toISOString();
// Whether the bursts held (`s`, the hook's store) still have to be loaded or
// extended back to `start`.
const short = (s, start) =>
  s.covered === null ||
  (start < s.covered && !(s.capped !== null && start >= s.capped));
export function useRadarBursts(mac, { from, refresh = 0, kind = null } = {}) {
  const [state, setState] = useState({
    bursts: [],
    loaded: false,
    error: null,
    offset: 0,
    unit: null,
    covered: null,
    truncated: null,
  });
  const [retry, setRetry] = useState(0);
  const store = useRef({
    bursts: [],
    covered: null,
    // The window start a load stopped short of (more bursts than one load
    // takes): not asked for again until the window grows further back.
    capped: null,
    loading: false,
    polling: false,
    reasking: false,
  });
  const firstRefresh = useRef(refresh);
  const publish = useCallback((result, patch = {}) => {
    const s = store.current;
    if (result) s.bursts = mergeBursts(s.bursts, result.bursts);
    const server = timeOf(result?.serverTime);
    setState((old) => ({
      ...old,
      ...patch,
      bursts: s.bursts,
      covered: s.covered,
      loaded: true,
      offset: server === null ? old.offset : server - Date.now(),
      unit: result?.unit ?? old.unit,
    }));
  }, []);
  const fetcher = useCallback(
    (signal) => (since, limit) =>
      rtctApi.radarBursts(
        mac,
        signal,
        kind ? { since, limit, kind } : { since, limit },
      ),
    [mac, kind],
  );

  // The window: load it, or extend it back.
  useEffect(() => {
    const s = store.current;
    if (!mac || !Number.isFinite(from) || !short(s, from)) return;
    const first = s.covered === null;
    const start = loadStart(s.bursts, s.covered, from);
    const abort = new AbortController();
    s.loading = true;
    pageBursts(fetcher(abort.signal), iso(start), {
      known: new Set(s.bursts.map(burstKey)),
      stopAtKnown: !first,
    })
      .then((result) => {
        if (abort.signal.aborted) return;
        s.covered = first ? start : Math.min(s.covered, start);
        s.loading = false;
        // An extension that stopped short of the window, by its budget.
        const capped = !first && start > from;
        if (!first) s.capped = capped ? from : null;
        publish(result, {
          error: null,
          truncated: result.why || (capped ? "cap" : null),
        });
        // The last day first; the rest of a longer window follows.
        if (first && start > from) setRetry((n) => n + 1);
      })
      .catch((e) => {
        if (abort.signal.aborted) return;
        s.loading = false;
        publish(null, { error: { message: e.message, status: e.status } });
      });
    return () => {
      abort.abort();
      s.loading = false;
    };
  }, [mac, from, retry, fetcher, publish]);

  // Newer bursts, with the page's refresh; a window that failed to load is
  // tried again instead.
  useEffect(() => {
    const s = store.current;
    if (
      refresh === firstRefresh.current ||
      !mac ||
      hidden() ||
      s.loading ||
      s.polling
    )
      return;
    if (short(s, from)) {
      setRetry((n) => n + 1);
      return;
    }
    const abort = new AbortController();
    s.polling = true;
    pageBursts(fetcher(abort.signal), pollSince(s.bursts, iso(from)), {
      known: new Set(s.bursts.map(burstKey)),
    })
      .then((result) => {
        if (abort.signal.aborted) return;
        s.polling = false;
        publish(result, { error: null });
      })
      .catch((e) => {
        if (abort.signal.aborted) return;
        s.polling = false;
        publish(null, { error: { message: e.message, status: e.status } });
      });
    return () => {
      abort.abort();
      s.polling = false;
    };
  }, [mac, from, refresh, fetcher, publish]);

  // Analyses the server has not made yet, among the bursts held: one page
  // from just before the oldest of them, every ANALYSIS_POLL_MS, until they
  // arrive or ANALYSIS_TRIES asks in a row bring nothing new; another burst
  // waiting starts it again.
  const [pendingKey, pendingAt] = useMemo(() => {
    const waiting = state.bursts.filter(analysisPending);
    return waiting.length
      ? [waiting.map(burstKey).join("|"), pendingSince(waiting)]
      : ["", null];
  }, [state.bursts]);
  const tries = useRef({ key: "", n: 0 });
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const s = store.current;
    if (!mac || !pendingKey || pendingAt === null) return;
    if (tries.current.key !== pendingKey)
      tries.current = { key: pendingKey, n: 0 };
    const t = tries.current;
    if (t.n >= ANALYSIS_TRIES) return;
    const abort = new AbortController();
    let mine = false;
    const timer = setTimeout(() => {
      // Not while the window loads or newer bursts are asked for (the merge
      // keeps the later copy of a burst, whichever answer arrives last).
      if (hidden() || s.loading || s.polling || s.reasking) {
        setTick((n) => n + 1);
        return;
      }
      s.reasking = mine = true;
      pageBursts(fetcher(abort.signal), pendingAt, { maxPages: 1 })
        .then((result) => {
          if (abort.signal.aborted) return;
          s.reasking = mine = false;
          const before = s.bursts;
          publish(result);
          t.n = s.bursts === before ? t.n + 1 : 0;
          setTick((n) => n + 1);
        })
        .catch(() => {
          if (abort.signal.aborted) return;
          s.reasking = mine = false;
          t.n += 1;
          setTick((n) => n + 1);
        });
    }, ANALYSIS_POLL_MS);
    return () => {
      clearTimeout(timer);
      abort.abort();
      if (mine) s.reasking = false;
    };
  }, [mac, pendingKey, pendingAt, tick, fetcher, publish]);
  return state;
}

// The full records (radar-captures/<id>/) of several captures - a burst's -
// three at a time, kept in `cache` (a ref holding a Map) like
// useRadarCaptureDetail's. Returns a Map of id (as text) -> {data, error};
// ids still loading are missing.
const NO_DETAILS = new Map();
export function useRadarCaptureDetails(ids, cache) {
  const key = (ids || [])
    .filter((id) => id !== null && id !== undefined)
    .map(String)
    .join("|");
  const [state, setState] = useState({ key: "", details: NO_DETAILS });
  useEffect(() => {
    const list = key ? key.split("|") : [];
    const results = new Map();
    for (const id of list) {
      const hit = cache.current.get(id);
      if (hit) results.set(id, { data: hit, error: "" });
    }
    setState({ key, details: new Map(results) });
    const queue = list.filter((id) => !results.has(id));
    if (!queue.length) return;
    const abort = new AbortController();
    const worker = async () => {
      while (queue.length && !abort.signal.aborted) {
        const id = queue.shift();
        try {
          const data = await rtctApi.radarCapture(id, abort.signal);
          if (abort.signal.aborted) return;
          remember(cache, id, data);
          results.set(id, { data, error: "" });
        } catch (e) {
          if (abort.signal.aborted) return;
          results.set(id, { data: null, error: e.message });
        }
        setState({ key, details: new Map(results) });
      }
    };
    for (let k = 0; k < 3; k++) worker();
    return () => abort.abort();
  }, [key, cache]);
  return state.key === key ? state.details : NO_DETAILS;
}

// Rain at the node over the page's window (GET devices/<mac>/rain/?days=,
// 1-31), asked again with the page's refresh; a failed reload keeps what is
// shown. -> {rain (readRain()), loaded, error, missing (no such endpoint
// yet: 404)}
export function useRadarRain(mac, days, refresh = 0) {
  const [state, setState] = useState({ key: null, data: null, error: null });
  const key = `${mac}|${days}`;
  useEffect(() => {
    if (!mac || hidden()) return;
    const abort = new AbortController();
    const id = `${mac}|${days}`;
    rtctApi
      .rain(mac, abort.signal, { days })
      .then((data) => {
        if (!abort.signal.aborted) setState({ key: id, data, error: null });
      })
      .catch((e) => {
        if (!abort.signal.aborted)
          setState((old) => ({
            key: id,
            data: old.key === id ? old.data : null,
            error: { message: e.message, status: e.status },
          }));
      });
    return () => abort.abort();
  }, [mac, days, refresh]);
  const current = state.key === key ? state : null;
  const data = current?.data ?? null;
  const rain = useMemo(() => readRain(data), [data]);
  return {
    rain,
    loaded: !!current,
    error: current?.error ?? null,
    missing: current?.error?.status === 404,
  };
}

// The analysis of the bursts over the last `days` (GET radar/analysis/):
// {points (oldest first), pending (bursts without an analysis yet), profile,
// thinning ({decimated, bursts, points}), loaded, error, missing}. Over more
// than a day the server thins the points out (max_points): rich readings
// bring one burst a wake. While some are pending it asks again every
// ANALYSIS_POLL_MS (each request analyses a few more), until ANALYSIS_TRIES
// answers in a row bring the count no lower. `missing`: the server has no
// such endpoint yet (404). A failed reload keeps what is shown.
export function useRadarAnalysis(mac, days, refresh = 0) {
  const [state, setState] = useState({ key: null, data: null, error: null });
  const [tick, setTick] = useState(0);
  const key = `${mac}|${days}`;
  useEffect(() => {
    if (!mac || hidden()) return;
    const abort = new AbortController();
    const id = `${mac}|${days}`;
    const maxPoints = analysisMaxPoints(days);
    rtctApi
      .radarAnalysis(mac, abort.signal, {
        days,
        ...(maxPoints ? { maxPoints } : {}),
      })
      .then((data) => {
        if (!abort.signal.aborted) setState({ key: id, data, error: null });
      })
      .catch((e) => {
        if (!abort.signal.aborted)
          setState((old) => ({
            key: id,
            data: old.key === id ? old.data : null,
            error: { message: e.message, status: e.status },
          }));
      });
    return () => abort.abort();
  }, [mac, days, refresh, tick]);
  const current = state.key === key ? state : null;
  const data = current?.data ?? null;
  const pending = Math.max(0, Math.round(finite(data?.pending) ?? 0));
  const stuck = useRef({ key: null, pending: null, n: 0 });
  useEffect(() => {
    if (!data || !pending || current?.error) return;
    const s = stuck.current;
    if (s.key !== key) stuck.current = { key, pending: null, n: 0 };
    const t = stuck.current;
    t.n = t.pending !== null && pending >= t.pending ? t.n + 1 : 0;
    t.pending = pending;
    if (t.n >= ANALYSIS_TRIES) return;
    const timer = setTimeout(() => setTick((n) => n + 1), ANALYSIS_POLL_MS);
    return () => clearTimeout(timer);
    // Each answer (a new state, even with the same data) decides the next ask.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);
  const points = useMemo(() => analysisPoints(data), [data]);
  const thinning = useMemo(
    () => analysisThinning(data, points),
    [data, points],
  );
  return {
    points,
    thinning,
    pending: data ? pending : null,
    profile: readProfile(data?.profile ?? null),
    loaded: !!current && (current.data !== null || current.error !== null),
    error: current?.error ?? null,
    missing: current?.error?.status === 404,
  };
}

// The node's empty-pipe profile (GET radar/profile/): {profile, loaded,
// error, suggestedGate (the gate the node's own estimate should search,
// readSuggestedGate()), windowKept (the server keeps the node's search
// window: the backend's .62 support, whose answers carry suggested_gate)};
// a 404 is kept as the error (no endpoint yet, or no profile).
export function useRadarProfile(mac, refresh = 0) {
  const [state, setState] = useState({ mac: null, data: null, error: null });
  useEffect(() => {
    if (!mac || hidden()) return;
    const abort = new AbortController();
    rtctApi
      .radarProfile(mac, abort.signal)
      .then((data) => {
        if (!abort.signal.aborted) setState({ mac, data, error: null });
      })
      .catch((e) => {
        if (!abort.signal.aborted)
          setState((old) => ({
            mac,
            data: old.mac === mac ? old.data : null,
            error: { message: e.message, status: e.status },
          }));
      });
    return () => abort.abort();
  }, [mac, refresh]);
  const current = state.mac === mac ? state : null;
  const data = current?.data;
  return {
    profile: readProfile(data),
    loaded: !!current && (current.data !== null || current.error !== null),
    error: current?.error ?? null,
    suggestedGate: readSuggestedGate(data),
    windowKept:
      !!data &&
      typeof data === "object" &&
      Object.prototype.hasOwnProperty.call(data, "suggested_gate"),
  };
}
